import React from 'react';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';

const FilteringControls = ({ 
  filters, 
  onFilterChange, 
  totalLeads = 0, 
  filteredCount = 0 
}) => {
  const handleFilterChange = (field, value) => {
    onFilterChange?.({
      ...filters,
      [field]: value
    });
  };

  const handleReset = () => {
    onFilterChange?.({
      validationStatus: 'all',
      dataCompleteness: 'all',
      confidenceLevel: 0
    });
  };

  const getFilteredPercentage = () => {
    if (totalLeads === 0) return 0;
    return Math.round((filteredCount / totalLeads) * 100);
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-foreground">Advanced Filtering</h3>
        <div className="flex items-center space-x-2">
          <div className="text-sm text-muted-foreground">
            Showing {filteredCount?.toLocaleString()} of {totalLeads?.toLocaleString()} leads
            {filteredCount !== totalLeads && (
              <span className="ml-1 text-primary">({getFilteredPercentage()}%)</span>
            )}
          </div>
          {(filters?.validationStatus !== 'all' || 
            filters?.dataCompleteness !== 'all' || 
            filters?.confidenceLevel > 0) && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleReset}
            >
              <Icon name="X" size={14} className="mr-1" />
              Clear Filters
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Select
          label="Validation Status"
          value={filters?.validationStatus}
          onChange={(value) => handleFilterChange('validationStatus', value)}
          options={[
            { value: 'all', label: 'All Status' },
            { value: 'validated', label: 'Validated' },
            { value: 'pending', label: 'Pending' },
            { value: 'needs_review', label: 'Needs Review' }
          ]}
        />

        <Select
          label="Data Completeness"
          value={filters?.dataCompleteness}
          onChange={(value) => handleFilterChange('dataCompleteness', value)}
          options={[
            { value: 'all', label: 'All Levels' },
            { value: 'high', label: 'High (80%+)' },
            { value: 'medium', label: 'Medium (50-79%)' },
            { value: 'low', label: 'Low (<50%)' }
          ]}
        />

        <div>
          <label className="block text-sm font-medium text-foreground mb-1">
            Min AI Confidence
          </label>
          <div className="flex items-center space-x-2">
            <Input
              type="range"
              min="0"
              max="100"
              step="5"
              value={filters?.confidenceLevel}
              onChange={(e) => handleFilterChange('confidenceLevel', parseInt(e?.target?.value))}
              className="flex-1"
            />
            <span className="text-sm text-muted-foreground min-w-[40px]">
              {filters?.confidenceLevel}%
            </span>
          </div>
        </div>

        <div className="flex items-end space-x-2">
          <Button
            variant="outline"
            className="flex-1"
            onClick={() => console.log('Export filtered leads')}
            disabled={filteredCount === 0}
          >
            <Icon name="Download" size={16} className="mr-2" />
            Export
          </Button>
          <Button
            variant="default"
            className="flex-1"
            onClick={() => console.log('Save current filters')}
            disabled={filteredCount === 0}
          >
            <Icon name="Bookmark" size={16} className="mr-2" />
            Save
          </Button>
        </div>
      </div>

      {/* Quick Filter Buttons */}
      <div className="flex items-center space-x-2 mt-4 pt-4 border-t border-border">
        <span className="text-sm text-muted-foreground">Quick filters:</span>
        <Button
          variant={filters?.validationStatus === 'needs_review' ? 'default' : 'outline'}
          size="sm"
          iconName="AlertTriangle"
          onClick={() => handleFilterChange('validationStatus', 
            filters?.validationStatus === 'needs_review' ? 'all' : 'needs_review'
          )}
        >
          Needs Review
        </Button>
        <Button
          variant={filters?.confidenceLevel >= 80 ? 'default' : 'outline'}
          size="sm"
          iconName="Award"
          onClick={() => handleFilterChange('confidenceLevel', 
            filters?.confidenceLevel >= 80 ? 0 : 80
          )}
        >
          High Confidence
        </Button>
        <Button
          variant={filters?.dataCompleteness === 'low' ? 'default' : 'outline'}
          size="sm"
          iconName="AlertCircle"
          onClick={() => handleFilterChange('dataCompleteness', 
            filters?.dataCompleteness === 'low' ? 'all' : 'low'
          )}
        >
          Incomplete Data
        </Button>
      </div>
    </div>
  );
};

export default FilteringControls;